import { Box, Tab, TabList, Tabs } from "@chakra-ui/react";
import { TabsProps } from "@chakra-ui/tabs";
import clsx from "clsx";
import { last } from "lodash-es";
import React, { useContext } from "react";
import {
  Navigate,
  Outlet,
  resolvePath,
  RouteObject,
  UNSAFE_RouteContext,
  useMatches,
  useNavigate,
  useNavigation,
} from "react-router-dom";
import { QueryBoundary } from "shared/api/QueryBoundary";
import { RouteHandle } from "shared/navigation/RouteHandle";
import { RouteId } from "shared/navigation/Routes";
import { useRouteBasePath } from "shared/navigation/UseRouteBasePath";
import invariant from "shared/utils/TinyInvariant";

export type PageTabsStatus = Partial<Record<RouteId, boolean>>;

export interface PageTabsProps {
  context?: unknown;
  disabledTabs?: PageTabsStatus;
  hiddenTabs?: PageTabsStatus;
  hideSingleTab?: boolean;
}

interface TabRouteHandle extends RouteHandle {
  isDisabled?: boolean;
  isHidden?: boolean;
}

export const PageTabs: React.FC<PageTabsProps> = ({
  context,
  hideSingleTab,
  ...props
}) => {
  const { tabsProps, tabs, tabRoutes, currentIndex } = usePageTabs(props);

  // If no tab is active, redirect to first (non-hidden) tab.
  if (currentIndex === -1 && tabRoutes.length > 0) {
    return (
      <Navigate
        to={tabRoutes[0].path!}
        replace={true}
        state={{
          isPageTab: true,
        }}
      />
    );
  }

  return (
    <Box
      display="contents"
      className={clsx({
        "mb-page-tabs": true,
        "mb-page-tabs-hidden": hideSingleTab && tabs.length === 1,
      })}
    >
      {hideSingleTab && tabs.length === 1 ? null : (
        <Tabs
          sx={{
            marginTop: "4px",
            ".chakra-tabs__tab": {
              alignItems: "flex-start",
              _before: {
                alignItems: "flex-start",
              },
            },
          }}
          {...tabsProps}
        >
          <TabList>
            {tabs.map(({ id, title, ...props }) => (
              <Tab key={id} title={title} {...props}>
                {title}
              </Tab>
            ))}
          </TabList>
        </Tabs>
      )}
      <QueryBoundary>
        <Outlet context={context} />
      </QueryBoundary>
    </Box>
  );
};

export interface UsePageTabsProps {
  disabledTabs?: Record<string, boolean>;
  hiddenTabs?: Record<string, boolean>;
}

export interface PageTabProps {
  id: string;
  title: string;
  isDisabled?: boolean;
}

export interface UsePageTabsResult {
  tabsProps: Partial<TabsProps>;
  tabs: PageTabProps[];
  tabRoutes: RouteObject[];
  currentIndex: number;
}

export const usePageTabs = ({
  disabledTabs,
  hiddenTabs,
}: UsePageTabsProps): UsePageTabsResult => {
  const navigate = useNavigate();
  const { location: navLocation } = useNavigation();
  const basePath = useRouteBasePath();

  // FIXME: Find a safer way to do this.
  const { matches: matchesForTabsPage } = useContext(UNSAFE_RouteContext);

  // The last route here is the page containing the <PageTabs/> component and
  // its children are the tabs.
  const pageTabsRoute = last(matchesForTabsPage)!.route;
  const tabRoutes = (pageTabsRoute.children ?? []).filter(
    (tab) =>
      !(tab.id && hiddenTabs && tab.id in hiddenTabs && hiddenTabs[tab.id]) &&
      !(tab.handle as TabRouteHandle | undefined)?.isHidden,
  );

  // The last route here is the child for the active tab, unless it's the
  // same as the pageTabsRoute, in which case there is no active tab.
  const matchesForChildRoutes = useMatches();
  const lastMatch = last(matchesForChildRoutes);
  const currentTab = lastMatch?.id !== pageTabsRoute.id ? lastMatch : undefined;

  const loadingTab = tabRoutes.find(
    (route) =>
      resolvePath(route.path!, basePath).pathname === navLocation?.pathname,
  );

  const currentIndex = tabRoutes.findIndex(
    (route) => route.id === (loadingTab?.id ?? currentTab?.id),
  );

  const tabsProps: Partial<TabsProps> = {
    isLazy: true,
    isManual: true,
    index: currentIndex,
    onChange: (index) => {
      navigate(tabRoutes[index].path!, { state: { isPageTab: true } });
    },
  };

  const tabs = tabRoutes.map(
    ({ id, handle: { title, isDisabled } }): PageTabProps => {
      invariant(id, "Tab route must have an id");
      return {
        id,
        title,
        isDisabled:
          isDisabled ||
          (disabledTabs && id in disabledTabs && disabledTabs[id]),
      };
    },
  );

  return {
    tabsProps,
    tabs,
    tabRoutes,
    currentIndex,
  };
};
